import type { Storage } from '@tour-kit/core';
import { LicenseProvider } from '@tour-kit/license';
import { MultiTourKitProvider } from '@tour-kit/react';
import { useEffect, useState, type ReactNode } from 'react';

import { GUIDANCE_TARGET } from '@/core/guidance/constants';
import { GetStartedChecklistRoot } from '@/core/guidance/get-started-checklist-root';
import { GuidanceAnnouncementsBridge } from '@/core/guidance/guidance-announcements-bridge';
import { GuidanceAnnouncementsProvider } from '@/core/guidance/guidance-announcements-root';
import { trackGuidanceEvent } from '@/core/guidance/guidance-analytics';
import { GuidanceHintsProvider } from '@/core/guidance/guidance-hints-root';
import { createGuidanceTourStorage } from '@/core/guidance/guidance-storage';
import { OrientationTour } from '@/core/guidance/orientation-tour';
import {
  getTourKitLicenseKey,
  getTourKitOrganizationId,
  shouldMountTourKitPro,
} from '@/core/guidance/tour-kit-license';
import { useGuidanceRouter } from '@/core/guidance/use-guidance-router';
import { useAuthStore } from '@/core/store/auth.store';

import '@tour-kit/react/styles/variables.css';
import '@tour-kit/react/styles/theme.css';
import '@tour-kit/react/styles/components.css';
import '@/core/guidance/guidance-theme.css';

type GuidanceProviderProps = {
  children: ReactNode;
};

function TourKitLicenseBoundary({ children }: { children: ReactNode }) {
  const licenseKey = getTourKitLicenseKey();
  if (!licenseKey) {
    return <>{children}</>;
  }
  const organizationId = getTourKitOrganizationId();
  return (
    <LicenseProvider
      licenseKey={licenseKey}
      organizationId={organizationId || undefined}
    >
      {children}
    </LicenseProvider>
  );
}

export function GuidanceProvider({ children }: GuidanceProviderProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const [storage, setStorage] = useState<Storage | null>(null);

  const [prevUserId, setPrevUserId] = useState(userId);
  if (userId !== prevUserId) {
    setPrevUserId(userId);
    if (!userId) {
      setStorage(null);
    }
  }

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    void createGuidanceTourStorage(userId).then((next) => {
      if (!cancelled) setStorage(next);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // The shell (`children`) never sits inside the TourKit providers: they mount
  // once storage is hydrated and the checklist provider is keyed, and wrapping
  // the shell would remount every tab page then (lost state, dropped clicks).
  return (
    <GuidanceAnnouncementsBridge
      guidance={
        userId && storage ? (
          <GuidanceProviders userId={userId} storage={storage} />
        ) : null
      }
    >
      {children}
    </GuidanceAnnouncementsBridge>
  );
}

function GuidanceProviders({
  userId,
  storage,
}: {
  userId: string;
  storage: Storage;
}) {
  const router = useGuidanceRouter();
  const mountPro = shouldMountTourKitPro();

  return (
    <TourKitLicenseBoundary>
      <MultiTourKitProvider
        router={router}
        autoNavigate
        onTourStart={(tourId) =>
          trackGuidanceEvent({ name: 'tour_started', tourId })
        }
        onTourComplete={(tourId) =>
          trackGuidanceEvent({ name: 'tour_completed', tourId })
        }
        onTourSkip={(tourId, stepIndex) =>
          trackGuidanceEvent({ name: 'tour_skipped', tourId, stepIndex })
        }
        onStepView={(tourId, stepId, stepIndex) =>
          trackGuidanceEvent({
            name: 'step_viewed',
            tourId,
            stepId,
            stepIndex,
          })
        }
        config={{
          persistence: {
            enabled: true,
            storage,
            keyPrefix: `tourkit:${userId}`,
            trackCompleted: true,
            dontShowAgain: true,
          },
          spotlight: {
            enabled: true,
            padding: 8,
            borderRadius: 12,
          },
          a11y: {
            reducedMotion: 'respect',
          },
        }}
      >
        <GuidanceHintsProvider storage={storage}>
          {mountPro ? (
            <GuidanceAnnouncementsProvider storage={storage}>
              <GetStartedChecklistRoot />
            </GuidanceAnnouncementsProvider>
          ) : null}
          {/* Outside the workspace-keyed checklist provider so a switch does not remount the tour. */}
          <div
            data-guidance-target={GUIDANCE_TARGET.welcomeHost}
            className="pointer-events-none fixed left-1/2 top-[40%] h-px w-px -translate-x-1/2"
            aria-hidden
          />
          <OrientationTour />
        </GuidanceHintsProvider>
      </MultiTourKitProvider>
    </TourKitLicenseBoundary>
  );
}
